

import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  MenuItem,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  addHeadline,
  getHeadlines,
  updateHeadline,
  getHeadlineById,
  deleteHeadline,
} from '../../../services/adminService';
import {
  getCountries,
  getStatesByCountry,
  getCitiesByState,
  getCategories,
  getSubcategories,
} from '../../../services/apiService';

const HeadlinePage = () => {
  const [formData, setFormData] = useState({
    headlineText: '',
    category: '',
    subCategory: '',
    country: '',
    state: '',
    district: '',
  });

  const [editingId, setEditingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [headlines, setHeadlines] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    loadHeadlines();
    loadCountries();
    loadCategories();
  }, []);

  const loadHeadlines = async () => {
    try {
      const res = await getHeadlines();
      setHeadlines(res.data);
    } catch (error) {
      console.error("Error loading headlines:", error);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await getCountries();
      setCountries(res.data);
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCountryChange = async (e) => {
    const countryId = e.target.value;
    setFormData({ ...formData, country: countryId, state: '', district: '' });
    try {
      const res = await getStatesByCountry(countryId);
      setStates(res.data);
      setCities([]);
    } catch (error) {
      console.error("Error loading states:", error);
      setStates([]);
      setCities([]);
    }
  };

  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setFormData({ ...formData, state: stateId, district: '' });
    try {
      const res = await getCitiesByState(stateId);
      setCities(res.data);
    } catch (error) {
      console.error("Error loading cities:", error);
      setCities([]);
    }
  };

  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setFormData({ ...formData, category: categoryId, subCategory: '' });
    try {
      const res = await getSubcategories(categoryId);
      setSubcategories(res.data);
    } catch (error) {
      console.error("Error loading subcategories:", error);
      setSubcategories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      headlineText: formData.headlineText,
      category: categories.find(cat => cat._id === formData.category)?.name || formData.category,
      subCategory: subcategories.find(sub => sub._id === formData.subCategory)?.name || formData.subCategory,
      country: countries.find(c => c._id === formData.country)?.name || formData.country,
      state: states.find(s => s._id === formData.state)?.name || formData.state,
      district: cities.find(d => d._id === formData.district)?.name || formData.district,
    };

    try {
      if (editingId) {
        await updateHeadline(editingId, payload);
        handleCloseDialog();
      } else {
        await addHeadline(payload);
        resetForm();
        setIsDialogOpen(false);
      }
      loadHeadlines();
    } catch (error) {
      console.error('Error submitting headline:', error);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await getHeadlineById(id);
      const data = res.data;

      let categoryIdToSet = '';
      let subCategoryIdToSet = '';
      let countryIdToSet = '';
      let stateIdToSet = '';
      let districtIdToSet = '';

      if (typeof data.category === 'string') {
        const foundCategory = categories.find(cat => cat.name === data.category);
        categoryIdToSet = foundCategory ? foundCategory._id : data.category;
      } else if (data.category?._id) {
        categoryIdToSet = data.category._id;
      }

      if (categoryIdToSet) {
        const subcatRes = await getSubcategories(categoryIdToSet);
        setSubcategories(subcatRes.data);
      }

      if (typeof data.subCategory === 'string') {
        const foundSubCategory = (await getSubcategories(categoryIdToSet)).data.find(sub => sub.name === data.subCategory);
        subCategoryIdToSet = foundSubCategory ? foundSubCategory._id : data.subCategory;
      } else if (data.subCategory?._id) {
        subCategoryIdToSet = data.subCategory._id;
      }

      countryIdToSet = data.country?._id || data.country;
      stateIdToSet = data.state?._id || data.state;
      districtIdToSet = data.district?._id || data.district;

      setFormData({
        headlineText: data.headlineText,
        category: categoryIdToSet,
        subCategory: subCategoryIdToSet,
        country: countryIdToSet,
        state: stateIdToSet,
        district: districtIdToSet,
      });
      setEditingId(id);

      if (countryIdToSet) {
        const statesData = await getStatesByCountry(countryIdToSet);
        setStates(statesData.data);
      }

      if (stateIdToSet) {
        const citiesData = await getCitiesByState(stateIdToSet);
        setCities(citiesData.data);
      }

      setIsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching headline for edit:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this headline?')) {
      try {
        await deleteHeadline(id);
        loadHeadlines();
        alert('Headline deleted successfully!');
      } catch (error) {
        console.error('Error deleting headline:', error);
        alert('Failed to delete headline.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      headlineText: '',
      category: '',
      subCategory: '',
      country: '',
      state: '',
      district: '',
    });
    setEditingId(null);
    setStates([]);
    setCities([]);
    setSubcategories([]);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const renderFormFields = () => (
    <>
      <TextField
        label="Headline Text"
        name="headlineText"
        value={formData.headlineText}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      />

      {/* <TextField
        label="Category"
        name="category"
        select
        value={formData.category}
        onChange={handleCategoryChange}
        fullWidth
        margin="normal"
        required
      >
        {categories.map((cat) => (
          <MenuItem key={cat._id} value={cat._id}>
            {cat.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Subcategory"
        name="subCategory"
        select
        value={formData.subCategory}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      >
        {subcategories.map((sub) => (
          <MenuItem key={sub._id} value={sub._id}>
            {sub.name}
          </MenuItem>
        ))}
      </TextField> */}

      <TextField
        select
        label="Country"
        name="country"
        value={formData.country}
        onChange={handleCountryChange}
        fullWidth
        margin="normal"
        required
      >
        {countries.map((country) => (
          <MenuItem key={country._id} value={country._id}>
            {country.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="State"
        name="state"
        select
        value={formData.state}
        onChange={handleStateChange}
        fullWidth
        margin="normal"
        required
      >
        {states.map((s) => (
          <MenuItem key={s._id} value={s._id}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="District"
        name="district"
        select
        value={formData.district}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      >
        {cities.map((c) => (
          <MenuItem key={c._id} value={c._id}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
    </>
  );

  return (
    <Grid container spacing={2} padding={2}>
      {/* Headline List */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Headline List</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
            >
              ➕ Add Headline
            </Button>
          </Box>
          {headlines.map((hl) => (
            <Box key={hl._id} sx={{ mb: 2 }}>
              <Typography fontWeight="bold">{hl.headlineText}</Typography>
              {/* <Typography variant="body2">
                {hl.category?.name || hl.category} / {hl.subCategory?.name || hl.subCategory}
              </Typography> */}
              <Typography variant="caption" color="text.secondary">
                {hl.country?.name || hl.country} → {hl.state?.name || hl.state} → {hl.district?.name || hl.district}
              </Typography>
              <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => handleEdit(hl._id)}>
                Edit
              </Button>
              <Button size="small" variant="outlined" color="error" sx={{ mt: 1, ml: 1 }} onClick={() => handleDelete(hl._id)}>
                Delete
              </Button>
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit Headline' : 'Add Headline'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>{renderFormFields()}</DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingId ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Grid>
  );
};

export default HeadlinePage;
